// Fotogrammetri og GEOINT: kapabilitetsmodell for en HETEROGEN dronepark.
//
// ── HVORFOR DENNE FILA FINNES ───────────────────────────────────────────────
// EIERSVAR 2026-08-18, på spørsmålene i GEOINT_FOTOGRAMMETRI_PLAN:
//   «Gir det termiske kameraet radiometriske data ODM kan lese?»
//        → «lag støtte for begge deler»
//   «Kan AGC slås av og temperaturspennet låses?»            → «på noen»
//   «Kommer gimbal-posen ut per bilde?»                      → «på noen»
//   «Maskinvare?»                          → «ikke tenk på jernet»
//
// Tre av fire svar er «det varierer». Det er ikke en detalj — det er selve
// arkitekturkravet: **vi kan ikke bygge én rørledning som antar den beste
// sensoren.** Hvert produkt må bære hva kilden FAKTISK kunne levere, og
// rørledningen må degradere synlig i stedet for å late som.
//
// ⚠ DEN FARLIGE FEILEN ER IKKE AT EN DÅRLIG SENSOR GIR ET DÅRLIG PRODUKT.
// Den farlige feilen er at et dårlig produkt ser ut som et godt. Et ortofoto
// uten bakkekontrollpunkter ser like skarpt ut som et med; en termisk mosaikk
// fra et kamera med AGC på ser like jevn ut som en med låst spenn — helt til
// noen måler i den, eller sammenligner to flyvninger og «finner» en endring som
// bare var forsterkningsregulering. Derfor er `produktForbehold()` ikke pynt:
// den er den delen av modulen som gjør kapabilitetsnivået synlig for en
// analytiker som ikke var med på flyvningen.
//
// Samme idiom som `Usikkerhetsform`/`SporUtenPosisjon` i track.ts: typesystemet
// skal gjøre det UMULIG å lese et degradert produkt som et fullverdig.
import { z } from 'zod';
import { TrackOrgSchema } from './track.js';
// ── Kapabilitetsakser ────────────────────────────────────────────────────────
/**
 * Hvordan produktet er knyttet til bakken.
 *
 * Forskjellen er en til to størrelsesordener i absolutt nøyaktighet, og den er
 * ikke synlig i bildet. `gnss` er dronens egen posisjon (meter, og verre under
 * jamming); `gcp` er innmålte bakkekontrollpunkter (desimeter).
 */
export const GeorefereringSchema = z.enum(['gcp', 'gnss', 'ukjent']);
/**
 * Hvor sensorens retning kommer fra. Avgjør om direktedrapering er mulig.
 *
 * ⚠ `plattform-imu` ER IKKE EN MÅLING AV HVOR KAMERAET PEKER. Den er
 * flykroppens holdning; en gimbal som beveger seg uavhengig gjør den irrelevant.
 * `gimbal-enkoder` er en faktisk vinkelmåling og er den eneste som er god nok
 * til å projisere en ramme på bakken — se advarselen i `maalestimat.ts` om at
 * 1,7° pitchfeil er 40× hele budsjettet over vann.
 */
export const PoseKildeSchema = z.enum(['gimbal-enkoder', 'nadir-laast', 'plattform-imu', 'ingen']);
/**
 * Hva en piksel i det termiske produktet BETYR.
 *
 * `radiometrisk`: verdien er temperatur (etter radiometrisk kalibrering).
 * `raa-dn`: verdien er et digitalt tall uten fysisk enhet. Ser identisk ut i en
 * gråtoneskala, og er den vanligste kilden til at noen leser av en «temperatur»
 * som ikke finnes. Kameraer som ikke legger en brukbar radiometrisk TIFF i
 * EXIF (nyere DJI R-JPEG) havner her med mindre vi henter ut temperaturen selv
 * i et forbehandlingssteg.
 */
export const TermiskKalibreringSchema = z.enum(['radiometrisk', 'raa-dn', 'ukjent']);
/**
 * Automatisk forsterkningsregulering under opptaket.
 *
 * ⚠ DETTE AVGJØR OM RAMMER KAN SAMMENLIGNES MED HVERANDRE. Med AGC på endrer
 * histogrammet seg mellom rammer, så samme objekt får ulik verdi i to
 * naborammer. Konsekvensene er to, og den andre er verst:
 *   1. Mosaikken får synlige skjøter (irriterende, men ærlig).
 *   2. En differanse mellom to flyvninger «finner» endringer som bare var
 *      forsterkningsregulering (usant, og det ser ut som etterretning).
 */
export const AgcTilstandSchema = z.enum(['laast', 'automatisk', 'ukjent']);
/**
 * Hva én sensor på én plattform faktisk kan levere.
 *
 * Registeret over disse ER flåtemodellen: «på noen» blir håndterbart først når
 * det står skrevet ned hvilke noen. Profilen settes én gang per
 * drone/kamera-kombinasjon (av den som satte opp materiellet), ikke gjettes per
 * flyvning.
 */
export const DronesensorProfilSchema = z.object({
    id: z.string().min(1).max(120),
    /** Menneskelesbart: «Zenmuse H20T (termisk)», «Mavic 3T RGB». */
    navn: z.string().min(1).max(200),
    /** Bildebredde/høyde i piksler — grunnlaget for GSD. */
    bredde: z.number().int().positive().max(60_000),
    hoyde: z.number().int().positive().max(60_000),
    /** Horisontalt og vertikalt synsfelt (grader). */
    hfovDeg: z.number().positive().max(180),
    vfovDeg: z.number().positive().max(180),
    /** Er dette et termisk (LWIR) kamera? */
    termisk: z.boolean().default(false),
    termiskKalibrering: TermiskKalibreringSchema.default('ukjent'),
    /** Kan AGC låses på denne sensoren? Eierens svar var «på noen». */
    agcKanLaases: z.boolean().default(false),
    poseKilde: PoseKildeSchema.default('ingen'),
    /**
     * 1σ pitch-usikkerhet (grader) for den poseKilden denne sensoren har.
     * Gimbal-enkoder: typisk 0,1–0,5°. Plattform-IMU: 1–2°.
     * ⚠ Tallet er en PÅSTAND som skal måles, ikke leses fra en brosjyre (GEO-16).
     */
    poseSigmaDeg: z.number().nonnegative().max(45).optional(),
    /** Org-tilhørighet — materiell hører til en enhet, og produktene arver scope. */
    org: TrackOrgSchema.optional(),
});
/**
 * Hvilket draperingsnivå denne sensoren gir.
 *
 * ⚠ `nadir-laast` KREVER AT NOEN HAR ERKLÆRT DET. Vi utleder aldri «dette ser
 * ut som en nadirflyvning» fra dataen: en skråstilt ramme behandlet som nadir
 * havner systematisk feil sted, og feilen vokser med flyhøyden uten å gi noe
 * varsel. Erklæringen ligger i profilen, satt av den som rigget materiellet.
 */
export function draperingsniva(p) {
    switch (p.poseKilde) {
        case 'gimbal-enkoder': return 'maalt';
        case 'nadir-laast': return 'nadir-antatt';
        case 'plattform-imu': return 'for-usikker';
        default: return 'ingen';
    }
}
export const DRAPERING_TEKST = {
    'maalt': 'Målt gimbal-pose',
    'nadir-antatt': 'Nadir-låst (pitch antatt −90°)',
    'for-usikker': 'Bare plattformholdning — for usikker til å plassere rammen',
    'ingen': 'Ingen retning oppgitt',
};
/** Kan rammen legges som bilde på kartet i det hele tatt? */
export function kanDraperes(niva) {
    return niva === 'maalt' || niva === 'nadir-antatt';
}
// ── Produktet ────────────────────────────────────────────────────────────────
export const FotogrammetriTypeSchema = z.enum(['ortofoto', 'dsm', 'dtm', 'termisk', 'objekthoyde']);
/**
 * Ett fotogrammetriprodukt: et lag som kan legges på SKADE-kartet.
 *
 * ⚠ `fraMs` OG `tilMs`, IKKE ETT TIDSPUNKT. En mosaikk er tidsutsmurt: den
 * dekker hele flyvningen. Å dekke 1 km² med et 640×512 termisk kamera på 100 m
 * tar ~25 minutter (se `dekningsrateM2PerTime`), så mosaikken viser 25
 * forskjellige minutter presentert som ett bilde. Et enkelt `tsMs` ville sagt at
 * den viser ett øyeblikk, og det er den mest naturlige feillesningen av et kart.
 */
export const FotogrammetriproduktSchema = z.object({
    id: z.string().min(1).max(200),
    type: FotogrammetriTypeSchema,
    navn: z.string().min(1).max(300),
    /** Innhentingsvinduet: første og siste ramme. */
    fraMs: z.number().int(),
    tilMs: z.number().int(),
    /** Da produktet ble ferdig prosessert. */
    produsertMs: z.number().int(),
    /** Bakkeoppløsning (cm/piksel). Regnet, ikke gjettet — se `gsdM`. */
    gsdCm: z.number().positive().max(10_000),
    georeferering: GeorefereringSchema,
    /** Antall rammer produktet er bygget av. 1 = enkeltramme, ikke en mosaikk. */
    rammer: z.number().int().nonnegative(),
    /** Sensorprofilen produktet kom fra (`DronesensorProfil.id`). */
    profilId: z.string().min(1).max(120).optional(),
    termiskKalibrering: TermiskKalibreringSchema.optional(),
    agc: AgcTilstandSchema.optional(),
    /** Flis-sti i hubben: `/geoint/fliser/{id}/{z}/{x}/{y}.png`. */
    flisSti: z.string().max(500).optional(),
    /** Full COG/GeoTIFF for videre analyse og eksport. */
    cogSti: z.string().max(500).optional(),
    /** Dekket område (bbox) — til kartlagsmenyen og til «zoom hit». */
    minLat: z.number(), minLon: z.number(), maxLat: z.number(), maxLon: z.number(),
    org: TrackOrgSchema.optional(),
    /** ODM-versjon + flagg, ordrett. Reproduserbarhet er en del av sporbarheten. */
    motor: z.string().max(500).optional(),
});
/**
 * Forbeholdene som SKAL vises sammen med produktet.
 *
 * ⚠ DETTE ER MODULENS VIKTIGSTE FUNKSJON. Alt over er felt; dette er det som
 * gjør feltene til noe en analytiker faktisk leser. Rekkefølgen er etter hvor
 * galt det kan gå: først det som gjør TALL feil (georeferering, kalibrering),
 * så det som gjør SAMMENLIGNINGER feil (AGC), så det som gjør TIDEN feil
 * (utsmøring). Tom liste betyr «ingen kjente forbehold» — og den skal være
 * vanskelig å oppnå.
 */
export function produktForbehold(p) {
    const ut = [];
    if (p.georeferering === 'gnss') {
        ut.push('⚠ Georeferert med dronens GNSS, uten bakkekontrollpunkter. Absolutt '
            + 'posisjon er meter-nøyaktig i beste fall, og dårligere under jamming. '
            + 'Relative mål i bildet er bedre enn absolutte koordinater.');
    }
    else if (p.georeferering === 'ukjent') {
        ut.push('⚠ Georefereringsmetoden er ikke oppgitt. Da er absolutt nøyaktighet ukjent '
            + '— ikke god, og ikke dårlig. Ikke bruk koordinater herfra til ildledelse.');
    }
    if (p.type === 'termisk') {
        if (p.termiskKalibrering === 'raa-dn') {
            ut.push('⚠ IKKE TEMPERATUR. Pikselverdiene er rå digitale tall uten fysisk enhet. '
                + 'De kan brukes til å se KONTRAST, aldri til å lese av grader.');
        }
        else if (p.termiskKalibrering === 'radiometrisk') {
            ut.push('Radiometrisk kalibrert. ⚠ Temperaturen forutsetter antatt emissivitet: '
                + 'vegetasjon og vann (≈0,98) leses godt, bar metall (≈0,1) kan leses '
                + 'titalls grader for kaldt — og metall er ofte nettopp det vi ser etter.');
        }
        else {
            ut.push('⚠ Ukjent om produktet er radiometrisk kalibrert. Behandle verdiene som kontrast, ikke temperatur.');
        }
        if (p.agc === 'automatisk') {
            ut.push('⚠ AGC var PÅ under opptaket. Samme objekt kan ha ulik verdi i to '
                + 'naborammer. Produktet kan ikke sammenlignes med en annen flyvning — '
                + 'en «endring» kan være ren forsterkningsregulering.');
        }
        else if (p.agc === 'ukjent') {
            ut.push('⚠ Ukjent AGC-tilstand. Sammenligning mot en annen flyvning er ikke etterprøvbar.');
        }
    }
    const spennMs = p.tilMs - p.fraMs;
    if (p.rammer > 1 && spennMs >= 120_000) {
        const min = Math.round(spennMs / 60_000);
        ut.push(`⚠ TIDSUTSMURT: rammene er tatt over ${min} minutter. Kartet viser ${min} `
            + 'forskjellige minutter, ikke ett øyeblikk. Noe kan ha flyttet seg mellom '
            + 'to overflyvninger av samme sted.');
    }
    if (p.rammer === 1) {
        ut.push('Enkeltramme, ikke en mosaikk. Geometrien er projisert, ikke rekonstruert — ikke mål i den.');
    }
    if (!p.motor) {
        ut.push('⚠ Prosesseringsmotor og flagg er ikke ført. Produktet kan ikke reproduseres.');
    }
    return ut;
}
/**
 * Kan to termiske produkter sammenlignes (endringsdeteksjon)?
 *
 * ⚠ SVARET ER OFTEST NEI, OG DET ER POENGET. Å differere to termiske flater som
 * ikke er sammenlignbare produserer et bilde full av «endringer» som er
 * sensorartefakter. Det ser ut som etterretning og er støy — den verste
 * kombinasjonen vi kan lage.
 */
export function kanSammenlignesTermisk(a, b) {
    if (a.type !== 'termisk' || b.type !== 'termisk') {
        return { ok: false, grunn: 'Begge produktene må være termiske.' };
    }
    if (a.agc !== 'laast' || b.agc !== 'laast') {
        return {
            ok: false,
            grunn: 'Krever låst AGC i BEGGE opptak. Med automatisk forsterkning kan samme '
                + 'objekt ha ulik verdi, og differansen viser reguleringen, ikke virkeligheten.',
        };
    }
    if (a.termiskKalibrering !== b.termiskKalibrering) {
        return { ok: false, grunn: 'Ulik kalibrering: temperatur kan ikke differeres mot rå digitale tall.' };
    }
    if (a.termiskKalibrering !== 'radiometrisk') {
        return {
            ok: false,
            grunn: 'Rå digitale tall er ikke sammenlignbare mellom opptak, selv med låst AGC — '
                + 'skalaen er ikke knyttet til noen fysisk størrelse.',
        };
    }
    // Selv når alt stemmer, er tidspunktet i døgnet en forutsetning: termisk
    // kontrast varierer med solinnstråling og kryssing (se GEO-15).
    return {
        ok: true,
        grunn: 'Sammenlignbare. ⚠ Forutsetter liknende tid på døgnet og værforhold — '
            + 'solinnstråling og termisk kryssing endrer kontrasten uten at noe på bakken har endret seg.',
    };
}
// ── Oppløsning og dekning: tallene som må ligge i kode, ikke i et notat ──────
/**
 * Bakkeoppløsning (m/piksel) rett under kameraet.
 *
 * `GSD = 2·h·tan(hfov/2) / bredde`
 *
 * ⚠ GJELDER NADIR OG FLATT LENDE. På skrå vokser GSD-en mot horisonten (og går
 * mot uendelig ved horisonten), så et skråbilde har ikke ÉN oppløsning. Bruk
 * dette til flyplanlegging og til Johnson-vurdering, ikke som en egenskap ved en
 * enkelt skråramme.
 */
export function gsdM(hoydeOverBakkenM, hfovDeg, bredde) {
    if (hoydeOverBakkenM <= 0 || bredde <= 0 || hfovDeg <= 0)
        return 0;
    return (2 * hoydeOverBakkenM * Math.tan((hfovDeg * Math.PI) / 360)) / bredde;
}
/** Stripebredde på bakken (m) ved nadir. */
export function stripebreddeM(hoydeOverBakkenM, hfovDeg) {
    if (hoydeOverBakkenM <= 0 || hfovDeg <= 0)
        return 0;
    return 2 * hoydeOverBakkenM * Math.tan((hfovDeg * Math.PI) / 360);
}
/**
 * Dekningsrate (m²/time) for en kartleggingsflyvning.
 *
 * ⚠ DETTE TALLET AVGJØR OM EN INNHENTINGSPLAN ER GJENNOMFØRBAR. Ved 100 m med
 * 45° HFOV, 20 % sidelapp og 10 m/s blir det ~2,4 km²/time — altså er én 1 km²
 * NAI omtrent ett batteri. Uten dette tallet i planleggingen blir
 * innhentingsplanen en ønskeliste.
 */
export function dekningsrateM2PerTime(hoydeOverBakkenM, hfovDeg, fartMPerS, sidelapp = 0.2) {
    const stripe = stripebreddeM(hoydeOverBakkenM, hfovDeg);
    const effektiv = stripe * (1 - Math.max(0, Math.min(0.95, sidelapp)));
    return Math.max(0, effektiv * Math.max(0, fartMPerS) * 3600);
}
/**
 * Johnson-kriteriene: hvor mange piksler over objektets kritiske mål trengs.
 *
 * Grove, veletablerte terskler (piksler ≈ 2 × linjepar): 2 for deteksjon
 * («noe er der»), 8 for gjenkjenning («det er et kjøretøy»), 13 for
 * identifikasjon («det er en lastebil av denne typen»).
 *
 * ⚠ DETTE ER EN NEDRE GRENSE FRA GEOMETRI ALENE. Kontrast, atmosfære, uskarphet
 * i bevegelse og operatørens tid foran skjermen kan alle gjøre det verre — aldri
 * bedre. Et system som lover gjenkjenning fordi pikslene holder, lover for mye.
 */
export const JOHNSON_PIKSLER = { deteksjon: 2, gjenkjenning: 8, identifikasjon: 13 };
/** Høyeste flyhøyde (m) som fortsatt gir ønsket Johnson-nivå for et objekt. */
export function maksHoydeForNiva(kritiskMaalM, niva, hfovDeg, bredde) {
    const kravGsd = kritiskMaalM / JOHNSON_PIKSLER[niva];
    if (kravGsd <= 0 || hfovDeg <= 0 || bredde <= 0)
        return 0;
    return (kravGsd * bredde) / (2 * Math.tan((hfovDeg * Math.PI) / 360));
}
/** Hvilket Johnson-nivå en gitt GSD faktisk gir for et objekt. */
export function johnsonNiva(kritiskMaalM, gsdMeter) {
    if (gsdMeter <= 0 || kritiskMaalM <= 0)
        return 'utilstrekkelig';
    const piksler = kritiskMaalM / gsdMeter;
    if (piksler >= JOHNSON_PIKSLER.identifikasjon)
        return 'identifikasjon';
    if (piksler >= JOHNSON_PIKSLER.gjenkjenning)
        return 'gjenkjenning';
    if (piksler >= JOHNSON_PIKSLER.deteksjon)
        return 'deteksjon';
    return 'utilstrekkelig';
}
/** Kritiske mål (m) for de objektene vi oftest vurderer. */
export const KRITISK_MAAL_M = {
    person: 0.5,
    motorsykkel: 1.0,
    personbil: 2.0,
    lastebil: 2.5,
    stridsvogn: 3.5,
    fartoy: 6.0,
};
//# sourceMappingURL=fotogrammetri.js.map